import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ImLocation } from "react-icons/im";
import { 
  FaBed, 
  FaBath, 
  FaRulerCombined, 
  FaPercent, 
  FaHandHoldingUsd,
  FaHeart,
  FaShare,
  FaTrash,
  FaEye
} from "react-icons/fa";
import { MdSecurity, MdVerified } from "react-icons/md";
import { motion } from "framer-motion";
import moment from "moment";

const MotionDiv = motion.div;

// Pakistani number formatting function
const formatPakistaniCurrency = (amount) => {
  if (!amount || amount === 0) return "Price on request";
  
  const num = Number(amount);
  
  if (num < 100000) {
    return `PKR ${num.toLocaleString()}`;
  }
  
  if (num >= 100000 && num < 10000000) {
    const lacs = num / 100000;
    if (Number.isInteger(lacs)) {
      return `PKR ${lacs} Lac${lacs > 1 ? 's' : ''}`;
    } else {
      return `PKR ${lacs.toFixed(1)} Lac${lacs > 1 ? 's' : ''}`;
    }
  }
  
  if (num >= 10000000 && num < 1000000000) {
    const crores = num / 10000000;
    if (Number.isInteger(crores)) {
      return `PKR ${crores} Crore${crores > 1 ? 's' : ''}`;
    } else {
      return `PKR ${crores.toFixed(1)} Crore${crores > 1 ? 's' : ''}`;
    }
  }
  
  if (num >= 1000000000) {
    const arab = num / 1000000000;
    if (Number.isInteger(arab)) {
      return `PKR ${arab} Arab${arab > 1 ? 's' : ''}`;
    } else {
      return `PKR ${arab.toFixed(1)} Arab${arab > 1 ? 's' : ''}`;
    }
  }
  
  return `PKR ${num.toLocaleString()}`;
};

// Helper function to check if property should show bedrooms/bathrooms
const shouldShowBedsAndBaths = (propertyType) => {
  if (!propertyType) return false;
  
  const type = propertyType.toLowerCase();
  const residentialTypes = [
    'house', 'apartment', 'flat', 'villa', 'townhouse', 
    'banglow', 'cottage', 'condo', 'duplex', 'penthouse',
    'residential', 'home', 'mansion', 'farmhouse'
  ];
  
  const landTypes = ['plot', 'land', 'agricultural land', 'commercial plot', 'residential plot'];
  
  if (landTypes.some(landType => type.includes(landType))) {
    return false;
  }
  
  return residentialTypes.some(resType => type.includes(resType));
};

const ListingItem = ({ id, property, deleteListing, editListing }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showInstallmentDetails, setShowInstallmentDetails] = useState(false);
  const navigate = useNavigate();

  const goToDetails = () => {
    navigate(`/category/${property.type || property.listingType}/${id}`);
  };

  const displayPrice = property.offer ? property.discountedPrice : property.price;
  const isRent = property.listingType === "rent" || 
                 property.type === "rent" || 
                 property.status === "for-rent";

  const hasInstallments = property.installmentAvailable === true;
  
  const installmentTotal = hasInstallments && property.downPayment && property.monthlyPayment && property.installmentDuration
    ? property.downPayment + (property.monthlyPayment * property.installmentDuration)
    : null;

  const showBedsAndBaths = shouldShowBedsAndBaths(property.propertyType);
  
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "Price on request";
    return formatPakistaniCurrency(amount);
  };

  const getPropertyIcon = () => {
    const type = property.propertyType?.toLowerCase() || "";
    if (type.includes("house") || type.includes("portion") || type.includes("banglow")) return "🏠";
    if (type.includes("apartment") || type.includes("flat")) return "🏢";
    if (type.includes("plot") || type.includes("land")) return "🌱";
    return "🏢";
  };

  return (
    <>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -8 }}
        className="w-full"
      >
        <div 
          className={`relative bg-white rounded-xl overflow-hidden border transition-all duration-500 cursor-pointer
            ${isHovered ? 'shadow-xl border-emerald-300' : 'shadow-md border-gray-200'}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={goToDetails}
        >
          {/* Image Section */}
          <div className="relative group overflow-hidden h-56 md:h-64 lg:h-72 bg-gray-100">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <img
              src={property?.propertyImages?.[0] || property?.thumbnail || property?.images?.[0] || "https://via.placeholder.com/600x400?text=Property+Image"}
              alt={property?.propertyType || "Property"}
              className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
            />
            
            {/* View Details Overlay */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 bg-black/50">
              <span className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg shadow-lg transform group-hover:scale-105 transition-transform duration-300 flex items-center gap-2">
                <FaEye size={14} />
                View Details
              </span>
            </div>
            
            {/* Top Badges */}
            <div className="absolute top-3 left-3 right-3 z-10">
              <div className="flex justify-between items-start gap-2">
                <div className="flex flex-wrap gap-1.5">
                  {property.featured && (
                    <span className="px-2 py-1 bg-emerald-600 text-white text-[10px] font-semibold rounded-full shadow-md flex items-center gap-1">
                      <MdVerified size={10} />
                      Featured
                    </span>
                  )}
                  {property.offer && (
                    <span className="px-2 py-1 bg-orange-500 text-white text-[10px] font-semibold rounded-full shadow-md flex items-center gap-1">
                      <FaPercent size={8} />
                      Offer
                    </span>
                  )}
                  {hasInstallments && (
                    <span className="px-2 py-1 bg-emerald-600 text-white text-[10px] font-semibold rounded-full shadow-md flex items-center gap-1">
                      <FaHandHoldingUsd size={8} />
                      Installments
                    </span>
                  )}
                </div>
                
                <div className="flex gap-1.5">
                  <button 
                    className="bg-white/90 backdrop-blur-sm p-1.5 rounded-full hover:bg-emerald-50 transition-all hover:scale-110 shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <FaHeart className="text-gray-500 hover:text-emerald-500 transition-colors text-xs" />
                  </button>
                  <button 
                    className="bg-white/90 backdrop-blur-sm p-1.5 rounded-full hover:bg-emerald-50 transition-all hover:scale-110 shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <FaShare className="text-gray-500 hover:text-emerald-500 transition-colors text-xs" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Price Overlay */}
            <div className="absolute bottom-3 right-3 z-10">
              <div className="bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-xl">
                <div className="text-white font-bold text-sm">
                  {formatCurrency(displayPrice)}
                  {isRent && <span className="text-[10px] ml-0.5">/mo</span>}
                </div>
                {property.offer && property.price && (
                  <div className="text-white/60 text-[10px] line-through text-right">
                    {formatCurrency(property.price)}{isRent && "/mo"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-4 bg-white">
            {/* Title and Type */}
            <div className="mb-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-lg">{getPropertyIcon()}</span>
                <span className="text-xs font-semibold text-emerald-600 capitalize">
                  {property.propertyType?.replace(/-/g, ' ') || 'Property'}
                </span>
                {property?.timestamp && (
                  <span className="ml-auto text-[10px] text-gray-400">
                    {moment(property.timestamp?.toDate()).fromNow()}
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold text-gray-800 line-clamp-1">
                {property.title || 'Property Title'}
              </h3>
            </div>

            {/* For Sale/Rent Badge */}
            <div className="mb-3">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-white
                ${isRent ? 'bg-indigo-600' : 'bg-emerald-600'}`}>
                {isRent ? "🏠 For Rent" : "💰 For Sale"}
              </span>
            </div>

            {/* Location */}
            <div className="flex items-center gap-1.5 mb-3 p-1.5 bg-gray-50 rounded-lg border border-gray-100">
              <ImLocation className="text-emerald-500 text-[10px] flex-shrink-0" />
              <span className="text-xs text-gray-600 truncate">
                {property.location}, {property.city}
              </span>
            </div>

            {/* Property Details Grid */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {showBedsAndBaths && property.bedrooms > 0 && (
                <div className="bg-gray-50 p-2 rounded-lg text-center border border-gray-100">
                  <FaBed className="text-emerald-500 mx-auto mb-0.5 text-xs" />
                  <div className="text-[10px] text-gray-500 uppercase">Beds</div>
                  <div className="text-xs font-semibold text-gray-700">{property.bedrooms}</div>
                </div>
              )}
              {showBedsAndBaths && property.bathrooms > 0 && (
                <div className="bg-gray-50 p-2 rounded-lg text-center border border-gray-100">
                  <FaBath className="text-emerald-500 mx-auto mb-0.5 text-xs" />
                  <div className="text-[10px] text-gray-500 uppercase">Baths</div>
                  <div className="text-xs font-semibold text-gray-700">{property.bathrooms}</div>
                </div>
              )}
              <div className="bg-gray-50 p-2 rounded-lg text-center border border-gray-100">
                <FaRulerCombined className="text-emerald-500 mx-auto mb-0.5 text-xs" />
                <div className="text-[10px] text-gray-500 uppercase">Area</div>
                <div className="text-xs font-semibold text-gray-700">
                  {property.marla ? `${property.marla}M` : property.area ? `${property.area}sqft` : '—'}
                </div>
              </div>
            </div>

            {/* Installment Details */}
            {hasInstallments && (
              <div className="mb-4 p-2 bg-gray-50 rounded-lg border border-emerald-100">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                      <FaHandHoldingUsd className="text-white text-[10px]" />
                    </div>
                    <span className="text-[10px] font-semibold text-emerald-700">Installment Plan</span>
                  </div>
                  <button 
                    className="text-[10px] text-emerald-600 hover:text-emerald-700 font-semibold"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInstallmentDetails(!showInstallmentDetails);
                    }}
                  >
                    {showInstallmentDetails ? '−' : '+'}
                  </button>
                </div>
                
                {showInstallmentDetails && (
                  <div className="mt-2 space-y-1">
                    <div className="grid grid-cols-3 gap-1 text-center">
                      {property.downPayment > 0 && (
                        <div>
                          <div className="text-[9px] text-gray-500">Down</div>
                          <div className="text-[10px] font-semibold text-emerald-600">{formatCurrency(property.downPayment)}</div>
                        </div>
                      )}
                      {property.monthlyPayment > 0 && (
                        <div>
                          <div className="text-[9px] text-gray-500">Monthly</div>
                          <div className="text-[10px] font-semibold text-emerald-600">{formatCurrency(property.monthlyPayment)}</div>
                        </div>
                      )}
                      {property.installmentDuration > 0 && (
                        <div>
                          <div className="text-[9px] text-gray-500">Duration</div>
                          <div className="text-[10px] font-semibold text-emerald-600">{property.installmentDuration}m</div>
                        </div>
                      )}
                    </div>
                    {installmentTotal && installmentTotal !== displayPrice && (
                      <div className="mt-1 p-1 bg-yellow-50 rounded text-center">
                        <span className="text-[9px] text-gray-600">Total: </span>
                        <span className="text-[10px] font-semibold text-emerald-700">{formatCurrency(installmentTotal)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1 pt-1 border-t border-gray-200">
                      <MdSecurity className="text-emerald-500 text-[10px]" />
                      <span className="text-[9px] text-gray-600">No Interest / Markup</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg shadow-md transition-all duration-300 hover:scale-[1.02] active:scale-95"
                onClick={goToDetails}
              >
                View Details
              </button>

              {(editListing || deleteListing) && (
                <div className="flex gap-2">
                  {editListing && (
                    <Link to={`/edit-listing/${id}`} className="flex-1" onClick={(e) => e.stopPropagation()}>
                      <button className="w-full py-1.5 border border-emerald-500 text-emerald-600 text-xs font-semibold rounded-lg hover:bg-emerald-50 transition-all">
                        Edit
                      </button>
                    </Link>
                  )}
                  {deleteListing && (
                    <button
                      className="flex-1 py-1.5 border border-red-500 text-red-500 text-xs font-semibold rounded-lg hover:bg-red-50 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteListing(property.id);
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </MotionDiv>
    </>
  );
};

export default ListingItem;